'use client';

import type { CatalogueItem } from './homepage-types';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { ScrollArrows, useScrollArrows } from './ui/scroll-arrows';

type CatalogueRailProps = {
  catalogues: CatalogueItem[];
  selectedId: number;
  onSelect: (id: number) => void;
  openLabel: string;
  previousLabel: string;
  nextLabel: string;
};

/**
 * Catalogue covers in a scroll-snap row with the design's columns (2 / 3 / 6); a click opens the
 * catalogue in the flipbook above. Once there are more catalogues than columns the row pages with
 * the arrows or a swipe; arrows stay hidden otherwise.
 */
export function CatalogueRail({
  catalogues,
  selectedId,
  onSelect,
  openLabel,
  previousLabel,
  nextLabel,
}: CatalogueRailProps) {
  const { ref, canScroll, scroll } = useScrollArrows<HTMLDivElement>();

  return (
    <div className="relative">
      {/* The padding keeps the cards' hover lift and tilt inside the scroll box. */}
      <div
        ref={ref}
        className="relative -mx-2 -my-3 grid snap-x snap-mandatory scroll-px-2 auto-cols-[calc((100%-16px)/2)] grid-flow-col gap-4 overflow-x-auto px-2 py-3 [scrollbar-width:none] min-[641px]:auto-cols-[calc((100%-32px)/3)] min-[1025px]:auto-cols-[calc((100%-80px)/6)] [&::-webkit-scrollbar]:hidden"
      >
        {catalogues.map(catalogue => {
          const isSelected = catalogue.id === selectedId;
          return (
            <button
              key={catalogue.id}
              type="button"
              aria-pressed={isSelected}
              onClick={() => onSelect(catalogue.id)}
              className="block cursor-pointer snap-start text-left transition-transform [transition-duration:250ms] hover:-translate-y-1.5 hover:-rotate-1"
            >
              {catalogue.image && (
                <Image
                  src={catalogue.image.url}
                  alt={catalogue.image.alt}
                  width={400}
                  height={543}
                  sizes="(min-width: 1025px) 200px, (min-width: 641px) 33vw, 50vw"
                  className={cn(
                    'mb-3 aspect-[3/4] w-full rounded-[14px] object-cover shadow-[0_18px_30px_-15px_rgba(0,0,0,.35)]',
                    // The offset colour is the section background.
                    isSelected &&
                      'ring-[3px] ring-[#00a1e6] ring-offset-[3px] ring-offset-[#f3f5f7]',
                  )}
                />
              )}
              <b className="block text-[.95rem]">{catalogue.title}</b>
              <span className="text-[.85rem] font-semibold text-[#00a1e6]">{openLabel}</span>
            </button>
          );
        })}
      </div>
      <ScrollArrows
        canScroll={canScroll}
        onScroll={scroll}
        previousLabel={previousLabel}
        nextLabel={nextLabel}
        className="top-[38%]"
      />
    </div>
  );
}
